import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { splitText } from 'griffo'

interface StepData {
	element: HTMLElement
	headingChars: HTMLSpanElement[]
	bodyChars: HTMLSpanElement[]
	headingRevert: () => void
	bodyRevert: () => void
}

export class RoutineScroll extends UcoastEl {
	static htmlSelector = 'routine-scroll'

	private outer: HTMLElement
	private steps: StepData[] = []
	private shapes: HTMLElement[] = []
	private reducedMotion: boolean
	private boundScrollHandler?: () => void
	private boundResizeHandler?: () => void
	private frameRequestId?: number
	private targetProgress = 0
	private smoothedProgress = 0
	private readonly smoothingFactor = 0.15
	private readonly smoothingEpsilon = 0.0005
	private readonly charStaggerTotal = 0.4
	private readonly fadeZone = 0.15

	/*
	 * Shape spotlight order per step.
	 * null = all shapes at 100%.
	 * Number = data-shape index to spotlight (others dim).
	 * Step 1: all visible, Step 2: small bottom circle, Step 3: oval, Step 4: medium top circle
	 */
	private readonly shapeOrder: (number | null)[] = [null, 2, 3, 1]
	private readonly dimOpacity = 0.15

	constructor() {
		super()
		this.outer = q.rs('.routine-scroll__outer', this)
		this.reducedMotion = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		).matches

		// Split text for each step
		const stepElements = Array.from(
			this.querySelectorAll<HTMLElement>('.routine-scroll__step')
		)
		for (const stepEl of stepElements) {
			const heading = q.os('.routine-scroll__heading', stepEl)
			const body = q.os('.routine-scroll__body', stepEl)

			let headingChars: HTMLSpanElement[] = []
			let bodyChars: HTMLSpanElement[] = []
			let headingRevert = () => {}
			let bodyRevert = () => {}

			if (heading) {
				const headingSplit = splitText(heading, { type: 'chars' })
				headingChars = headingSplit.chars
				headingRevert = headingSplit.revert
			}
			if (body) {
				const bodySplit = splitText(body, { type: 'chars' })
				bodyChars = bodySplit.chars
				bodyRevert = bodySplit.revert
			}

			// Set will-change and display on each char
			for (const char of headingChars) {
				char.style.willChange = 'opacity, filter, transform'
				char.style.display = 'inline-block'
			}
			for (const char of bodyChars) {
				char.style.willChange = 'opacity, filter, transform'
				char.style.display = 'inline-block'
			}

			this.steps.push({
				element: stepEl,
				headingChars,
				bodyChars,
				headingRevert,
				bodyRevert,
			})
		}

		// Query SVG shapes
		this.shapes = Array.from(
			this.querySelectorAll<HTMLElement>('[data-shape]')
		)
		for (const shape of this.shapes) {
			shape.style.transition = 'opacity 0.3s ease'
		}

		if (this.reducedMotion) {
			this.showAll()
			return
		}

		this.boundScrollHandler = this.onScroll.bind(this)
		this.boundResizeHandler = this.onScroll.bind(this)

		window.addEventListener('scroll', this.boundScrollHandler, {
			passive: true,
		})
		window.addEventListener('resize', this.boundResizeHandler)

		// Initial render
		this.onScroll()
	}

	private getProgress(): number {
		const rect = this.outer.getBoundingClientRect()
		const outerHeight = this.outer.offsetHeight
		const viewportHeight = window.innerHeight
		const scrollRange = outerHeight - viewportHeight

		if (scrollRange <= 0) return 0

		const progress = -rect.top / scrollRange
		return Math.max(0, Math.min(1, progress))
	}

	private onScroll() {
		this.targetProgress = this.getProgress()
		this.requestRender()
	}

	private requestRender() {
		if (this.frameRequestId) return
		this.frameRequestId = window.requestAnimationFrame(() => this.tick())
	}

	private tick() {
		this.frameRequestId = undefined
		const delta = this.targetProgress - this.smoothedProgress

		if (Math.abs(delta) > this.smoothingEpsilon) {
			this.smoothedProgress += delta * this.smoothingFactor
		} else {
			this.smoothedProgress = this.targetProgress
		}

		this.render(this.smoothedProgress)

		if (
			Math.abs(this.targetProgress - this.smoothedProgress) >
			this.smoothingEpsilon
		) {
			this.requestRender()
		}
	}

	private render(progress: number) {
		this.updateText(progress)
		this.updateShapes(progress)
	}

	private updateText(progress: number) {
		const stepCount = this.steps.length
		if (stepCount === 0) return

		const segmentSize = 1 / stepCount

		for (let i = 0; i < stepCount; i++) {
			const step = this.steps[i]
			const segmentStart = i * segmentSize
			const segmentEnd = segmentStart + segmentSize
			const fadeInEnd = segmentStart + segmentSize * this.fadeZone
			const fadeOutStart = segmentEnd - segmentSize * this.fadeZone

			// Determine local progress within this step's segment
			const localProgress =
				(progress - segmentStart) / (segmentEnd - segmentStart)

			if (progress < segmentStart || progress > segmentEnd) {
				// Fully outside — hide all chars
				this.setCharsState(step.headingChars, 0, true)
				this.setCharsState(step.bodyChars, 0, true)
				step.element.style.pointerEvents = 'none'
			} else if (i === 0 && progress <= fadeInEnd) {
				// First step: skip fade-in, show immediately
				this.setCharsState(step.headingChars, 1, true)
				this.setCharsState(step.bodyChars, 1, true)
				step.element.style.pointerEvents = 'auto'
			} else if (progress <= fadeInEnd) {
				// Fade-in zone
				const fadeProgress =
					(progress - segmentStart) / (fadeInEnd - segmentStart)
				this.animateChars(step.headingChars, fadeProgress, true)
				this.animateChars(step.bodyChars, fadeProgress, true)
				step.element.style.pointerEvents = 'auto'
			} else if (i === stepCount - 1 && progress >= fadeOutStart) {
				// Last step: skip fade-out, keep text visible past the last frame
				this.setCharsState(step.headingChars, 1, true)
				this.setCharsState(step.bodyChars, 1, true)
				step.element.style.pointerEvents = 'auto'
			} else if (progress >= fadeOutStart) {
				// Fade-out zone
				const fadeProgress =
					(progress - fadeOutStart) / (segmentEnd - fadeOutStart)
				const inverseFade = 1 - fadeProgress
				this.animateChars(step.headingChars, inverseFade, false)
				this.animateChars(step.bodyChars, inverseFade, false)
				step.element.style.pointerEvents = 'auto'
			} else {
				// Fully visible — middle zone
				this.setCharsState(step.headingChars, 1, true)
				this.setCharsState(step.bodyChars, 1, true)
				step.element.style.pointerEvents = 'auto'
			}
		}
	}

	private animateChars(
		chars: HTMLSpanElement[],
		fadeProgress: number,
		entering: boolean
	) {
		const charCount = chars.length
		if (charCount === 0) return

		for (let i = 0; i < charCount; i++) {
			const charNorm = charCount > 1 ? i / (charCount - 1) : 0
			// Stagger: early chars animate first
			const charStart = charNorm * this.charStaggerTotal
			const charEnd = charStart + (1 - this.charStaggerTotal)
			const charProgress = Math.max(
				0,
				Math.min(1, (fadeProgress - charStart) / (charEnd - charStart))
			)

			const opacity = charProgress
			const blur = (1 - charProgress) * 8
			const yDirection = entering ? 1 : -1
			const translateY = (1 - charProgress) * 30 * yDirection
			const scale = 0.92 + charProgress * 0.08

			const char = chars[i]
			char.style.opacity = `${opacity}`
			char.style.filter = blur > 0.1 ? `blur(${blur.toFixed(1)}px)` : 'none'
			char.style.transform = `translateY(${translateY.toFixed(1)}px) scale(${scale.toFixed(3)})`
		}
	}

	private setCharsState(
		chars: HTMLSpanElement[],
		opacity: number,
		entering: boolean
	) {
		for (const char of chars) {
			char.style.opacity = `${opacity}`
			if (opacity === 1) {
				char.style.filter = 'blur(0px)'
				char.style.transform = 'translateY(0px) scale(1)'
			} else {
				const yDirection = entering ? 1 : -1
				char.style.filter = 'blur(8px)'
				char.style.transform = `translateY(${30 * yDirection}px) scale(0.92)`
			}
		}
	}

	private updateShapes(progress: number) {
		const stepCount = this.steps.length
		if (stepCount === 0) return

		// Before entering or at progress 0, all shapes visible
		const activeIndex = Math.min(
			Math.floor(progress * stepCount),
			stepCount - 1
		)
		const activeShape = this.shapeOrder[activeIndex] ?? null

		if (activeShape === null) {
			// All shapes at full opacity (step 1 or before entering)
			for (const shape of this.shapes) {
				shape.style.opacity = '1'
			}
		} else {
			for (const shape of this.shapes) {
				const shapeIndex = parseInt(shape.getAttribute('data-shape') ?? '-1', 10)
				shape.style.opacity = shapeIndex === activeShape ? '1' : `${this.dimOpacity}`
			}
		}
	}

	private showAll() {
		for (const step of this.steps) {
			for (const char of step.headingChars) {
				char.style.opacity = '1'
				char.style.filter = 'none'
				char.style.transform = 'none'
			}
			for (const char of step.bodyChars) {
				char.style.opacity = '1'
				char.style.filter = 'none'
				char.style.transform = 'none'
			}
			step.element.style.position = 'relative'
			step.element.style.opacity = '1'
		}
		for (const shape of this.shapes) {
			shape.style.opacity = '1'
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		if (this.boundScrollHandler) {
			window.removeEventListener('scroll', this.boundScrollHandler)
		}
		if (this.boundResizeHandler) {
			window.removeEventListener('resize', this.boundResizeHandler)
		}
		if (this.frameRequestId) {
			window.cancelAnimationFrame(this.frameRequestId)
			this.frameRequestId = undefined
		}
	}
}
